import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { Collapse, cn, Scroller } from '@divlab/divanui';
import { IconChevronBottom } from '@divlab/divanui/icons';

import { useDeps } from '@Contexts/DI';
import Img from '@UI/Img';
import styles from './ConfiguratorTab.module.css';

import type { ScrollState } from '@divlab/divanui';
import type { HTMLAttributes, ReactElement, MouseEvent } from 'react';
import type { ConstructorValueData } from '@Pages/PageProduct/typings';
import type { ConfiguratorTabData } from '../../Configurator/Configurator';

export interface ConfiguratorTabProps extends HTMLAttributes<HTMLDivElement> {
  tab: ConfiguratorTabData;
  className?: string;
  slotHeader?: ReactElement;
  slotFooter?: ReactElement;
  count: number;
  selected: ConstructorValueData;
  isActive: boolean;
  onClickTab?: (tab: ConfiguratorTabData) => void;
}

const ConfiguratorTab = (props: ConfiguratorTabProps) => {
  const {
    className,
    tab,
    children,
    slotHeader,
    slotFooter,
    count,
    selected,
    isActive,
    onClickTab,
    ...restProps
  } = props;
  const [scrollPosition, setScrollPosition] = useState<ScrollState>(null);
  const { analytics } = useDeps();

  const handleClickHeader = useCallback(() => {
    analytics.dispatchEvent('constructor.productPage.parameterTab', {
      action: !isActive ? 'open' : 'close',
      label: tab.name,
    });

    if (onClickTab) onClickTab(tab);
  }, [analytics, onClickTab, tab, isActive]);

  const handleScroll = useCallback((e: MouseEvent, state: ScrollState) => {
    setScrollPosition(state);
  }, []);

  const hasScroll = useMemo(() => {
    return scrollPosition && scrollPosition !== 'top' && scrollPosition !== 'none';
  }, [scrollPosition]);

  useEffect(() => {
    if (!isActive) return;

    setTimeout(() => {
      analytics.dispatchEvent('constructor.productPage.parameterTab', {
        action: 'open',
        label: tab.name,
      });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [analytics, tab.name]);

  return (
    <div {...restProps} className={cn(styles.tab, { [styles.collapsed]: !isActive }, className)}>
      <div
        className={cn(styles.header, {
          [styles.bordered]: hasScroll && !slotHeader,
        })}
        onClick={handleClickHeader}
      >
        <div className={styles.name}>
          {tab.name}
          <span className={styles.count}>{`(${count})`}</span>
        </div>

        {selected && (
          <div className={styles.selected}>
            <Img src={selected.image} className={styles.preview} />
            <div className={styles.selectedText}>{selected.title}</div>
          </div>
        )}
        <div className={styles.right}>
          <IconChevronBottom className={styles.chevron} />
        </div>
      </div>
      <Collapse collapsed={!isActive}>
        <div>
          {slotHeader && (
            <div className={cn(styles.container, { [styles.bordered]: hasScroll }, styles.filters)}>
              {slotHeader}
            </div>
          )}

          <Scroller onChangeState={handleScroll} cnContainer={styles.content}>
            <div className={styles.container}>{children}</div>
          </Scroller>
          {slotFooter && slotFooter}
        </div>
      </Collapse>
    </div>
  );
};

export default memo(ConfiguratorTab);
